import React, { useState } from "react";
import { Drawer } from "@mui/material";
import CustomTooltip from "@product-app/common/components/CustomTooltip";
import { WorkflowCenterBar } from "@product-app/common/workflows/components/WorkflowCenterBar";
import { useAuth } from "../auth/useAuth";
import { oraLogoUrl } from "../assetUrl";
import FlowPasswordManager from "./FlowPasswordManager";

/**
 * `needs_you` is the QUEUE-FIRST tab (story pinned-pane, AC-2): the pane opens
 * on it and stays there. Launch is the catalog. The old Active tab (In flight
 * / All workflows) is retired — a running hop opens the live drawer.
 */
export type AppShellTab = "needs_you" | "launch" | "active";

export interface AppShellProps {
  children: React.ReactNode;
  demoMode?: boolean;
  onToggleDemo?: () => void;
  currentTab?: AppShellTab;
  onTabChange?: (tab: AppShellTab) => void;
  activeCount?: number;
  /**
   * Badge on the queue-first tab. This is the SERVER's `count` from
   * `list_waiting_for_me`, passed straight through — never a length
   * accumulated from push frames (design Constraint 19).
   */
  needsYouCount?: number;
  onSignOut?: () => void;
}

export const AppShell: React.FC<AppShellProps> = ({
  children,
  demoMode,
  onToggleDemo,
  currentTab,
  onTabChange,
  activeCount,
  needsYouCount,
  onSignOut,
}) => {
  const { isAuthenticated, signOut } = useAuth();

  const handleSignOut = () => {
    onSignOut?.();
    void signOut();
  };

  // ── Flow password panel state ──────────────────────────────────────────────
  const [flowPasswordsOpen, setFlowPasswordsOpen] = useState(false);

  if (isAuthenticated) {
    return (
      <div className="flex h-screen flex-col bg-primary-gray-light-50 font-sans">
        <div
          className="flex flex-shrink-0 items-center gap-2 border-b border-primary-gray-light-200 bg-base-white px-4 py-3"
          data-testid="appshell-today-header"
        >
          <span className="h-[9px] w-[9px] rounded-full border border-black bg-sunrise" />
          <span
            className="text-[15px] font-medium text-primary-gray-light-900"
            data-testid="appshell-title"
          >
            ora · Today
          </span>
          <span className="ml-auto text-[11px] text-primary-gray-light-400">
            pinned
          </span>
          <button
            type="button"
            onClick={handleSignOut}
            className="rounded border-none bg-transparent px-2 py-1 text-xs tracking-[0.02em] text-primary-gray-light-400 hover:text-primary-gray-light-600"
          >
            Sign out
          </button>
        </div>
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto" data-testid="appshell-content">
          {children}
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen flex-col bg-primary-gray-light-50 font-sans">
      {/* ── Brand Hero (unauthenticated / login) ─────────────────── */}
      <div
        className="relative flex-shrink-0 text-primary-gray-light-700"
        data-testid="appshell-hero"
      >
        {isAuthenticated && (
          <>
            {onToggleDemo && (
              <div className="absolute left-3 top-3">
                {/* ora's tooltip, not a native title (story 28633). Node
                    title + describeChild: "Demo" stays the button's name. */}
                <CustomTooltip
                  id="appshell-demo-toggle"
                  title={
                    <span>
                      {demoMode
                        ? "Demo mode on — click to turn off"
                        : "Turn on demo mode"}
                    </span>
                  }
                  describeChild
                  placement="bottom-start"
                  arrow
                  isOnModal
                >
                  <button
                    type="button"
                    onClick={onToggleDemo}
                    className={
                      "rounded-[10px] px-2 py-[2px] text-2xs font-semibold tracking-[0.01em] transition-colors " +
                      (demoMode
                        ? "border border-primary-warning-300 bg-primary-warning-50 text-primary-warning-700"
                        : "border border-primary-gray-light-200 bg-transparent text-primary-gray-light-400 hover:text-primary-gray-light-600")
                    }
                  >
                    {demoMode ? "● Demo" : "Demo"}
                  </button>
                </CustomTooltip>
              </div>
            )}
            <div className="absolute right-3 top-3 flex flex-row items-center gap-1">
              <button
                type="button"
                onClick={() => setFlowPasswordsOpen(true)}
                className="rounded border-none bg-transparent px-2 py-1 text-xs tracking-[0.02em] text-primary-gray-light-400 hover:text-primary-gray-light-600"
                aria-label="Manage flow passwords"
                data-testid="appshell-flow-passwords-btn"
              >
                Flow passwords
              </button>
              <button
                type="button"
                onClick={handleSignOut}
                className="rounded border-none bg-transparent px-2 py-1 text-xs tracking-[0.02em] text-primary-gray-light-400 hover:text-primary-gray-light-600"
              >
                Sign out
              </button>
            </div>

            {/* Flow passwords drawer — standing self-serve panel */}
            <Drawer
              anchor="bottom"
              open={flowPasswordsOpen}
              onClose={() => setFlowPasswordsOpen(false)}
              PaperProps={{
                sx: {
                  maxHeight: "80vh",
                  borderRadius: "12px 12px 0 0",
                  overflowY: "auto",
                },
              }}
            >
              <FlowPasswordManager onClose={() => setFlowPasswordsOpen(false)} />
            </Drawer>
          </>
        )}

        {/* The "oraWorkflows" brand bar — the SAME component every takeover
            wears (story 28633), so the logo never changes between screens. */}
        <WorkflowCenterBar logoSrc={oraLogoUrl()} testId="appshell-compact-label" />
      </div>

      {/* ── Tab bar ─────────────────────────────────────────────── */}
      {isAuthenticated && currentTab && onTabChange && (
        <div
          className="flex flex-shrink-0 border-b border-primary-gray-light-200 bg-base-white"
          role="tablist"
        >
          {/* FIRST in the strip and the default on open — the AM's day starts
              with what is waiting on them, not with a launch form. */}
          <TabButton
            tab="needs_you"
            label="Needs you"
            currentTab={currentTab}
            onTabChange={onTabChange}
            badge={needsYouCount}
          />
          <TabButton
            tab="launch"
            label="Launch"
            currentTab={currentTab}
            onTabChange={onTabChange}
          />
        </div>
      )}

      {/* ── Main content ─────────────────────────────────────────── */}
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto" data-testid="appshell-content">
        {children}
      </div>
    </div>
  );
};

interface TabButtonProps {
  tab: AppShellTab;
  label: string;
  currentTab: AppShellTab;
  onTabChange: (tab: AppShellTab) => void;
  badge?: number;
}

const TabButton: React.FC<TabButtonProps> = ({
  tab,
  label,
  currentTab,
  onTabChange,
  badge,
}) => {
  const active = currentTab === tab;
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={() => onTabChange(tab)}
      className={
        "relative flex-1 px-4 py-2 text-xs font-semibold uppercase tracking-[0.05em] transition-colors " +
        (active
          ? "text-primary-gray-light-900"
          : "text-primary-gray-light-500 hover:text-primary-gray-light-700")
      }
    >
      <span>{label}</span>
      {badge !== undefined && badge > 0 && (
        <span className="ml-1 rounded-full bg-sunrise px-2 py-[1px] text-2xs font-bold text-primary-gray-light-900">
          {badge}
        </span>
      )}
      <span
        className={
          "absolute inset-x-0 bottom-0 h-[2px] transition-transform duration-200 ease-out " +
          (active ? "scale-x-100 bg-sunrise" : "scale-x-0 bg-transparent")
        }
      />
    </button>
  );
};

export default AppShell;
